/**
 * « Tes séances » — le bloc de l'accueil (spec/seances.md).
 *
 * Une file courte de séances proposées, rechargée à chaque action : ce que
 * l'équipe attend (campagne, challenge, parcours), ce que l'évaluation a fait
 * ressortir, ce que la dernière partie montre. Une étiquette dit d'où vient
 * chaque ligne ; le bouton ouvre exactement l'endroit prévu. Aucun réglage.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { CalendarDays, Play, Check, ChevronRight } from "lucide-react";
import { Button } from "../ui/button";
import { planApi, type MySeancesPayload, type Seance } from "../../lib/api";
import type { AIAgent } from "../../types";
import { fromISODate, toISODate, weekStart, addDays } from "../../lib/plan-core";
import { launchDrill } from "../../lib/drill-launch";
import { SimIcon } from "./SimIcon";
import { Sparkline } from "./Sparkline";
import { BlockingError, useFailureCount } from "../BlockingError";
import { CriterionHelp, CriterionInline } from "../CriterionText";
import { canBuyLicences, REABONNEMENT_URL } from "../../lib/encadrant";
import { useDrillVisible } from "../../hooks/useDrillVisible";

const SOURCE_STYLE: Record<Seance["source"], { bg: string; color: string }> = {
  campaign: { bg: "#E7ECF7", color: "#384872" },
  challenge: { bg: "#FBF1D6", color: "#926B00" },
  path: { bg: "#EEEAF9", color: "#5A4BBE" },
  evaluation: { bg: "#E3F1FD", color: "#2A5C8A" },
  replay: { bg: "#E6F3EB", color: "#1E7A3C" },
  assessment: { bg: "#E3F1FD", color: "#2A5C8A" },
};

/**
 * Décision Q6 de Valérie (30/09/2026) : RH et GES ne s'entraînent pas — le bloc « Mes sessions » ne
 * leur est pas monté (accueil, page Entraînement). Les écrans qui montent le bloc appellent cette
 * règle, jamais une liste recopiée.
 */
export const MY_SEANCES_HIDDEN_PROFILES = ["RH", "GES"] as const;
export function showsMySeances(profileType: string | null | undefined): boolean {
  return !(MY_SEANCES_HIDDEN_PROFILES as readonly string[]).includes(profileType ?? "");
}

/**
 * Texte « pas encore accès » selon le profil de la personne (Q6, 30/09/2026) : un ORG peut acheter ou
 * s'attribuer une licence lui-même (Mon compte › onglet des licences) ; les autres s'adressent à
 * leur responsable.
 */
export function noAccessKey(profileType: string | null | undefined): "seances.noAccessOrg" | "seances.noAccess" {
  return profileType === "ORG" ? "seances.noAccessOrg" : "seances.noAccess";
}

/**
 * Z4 (décision Valérie du 30/09/2026) : l'accès du compte aux simulateurs est terminé. UN seul
 * message remplace les sessions (datées, échéances, suggestions) ; le bouton de réabonnement ne
 * s'affiche qu'à qui peut acheter (`canBuyLicences`), vers la destination unique `REABONNEMENT_URL`.
 * Partagé par « Mes sessions » / « Ma semaine » et la fiche membre de Pilot.
 */
/** Texte de fin d'accès : la personne elle-même, ou quelqu'un qui consulte sa fiche (R2 du 30/09/2026). */
export function accessEndedKey(observer: boolean): "seances.accessEnded" | "seances.accessEndedAccount" {
  return observer ? "seances.accessEndedAccount" : "seances.accessEnded";
}

export function AccessEndedNotice({ canResubscribe, observer = false }: {
  canResubscribe: boolean;
  /** Lu par quelqu'un d'autre que la personne (fiche d'un membre dans Pilot) : décision R2 du 30/09/2026. */
  observer?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div data-access-ended className="flex flex-col items-start gap-2 rounded-lg bg-white px-3 py-3">
      <p className="text-sm font-semibold" style={{ color: "#384872" }}>{t(accessEndedKey(observer))}</p>
      {canResubscribe && (
        <Button size="sm" onClick={() => { window.location.href = REABONNEMENT_URL; }}>{t("quota.ctaSubscribe")}</Button>
      )}
    </div>
  );
}

/** Événement global « une séance a pu être soldée » — émis à la fin d'une partie, d'un exercice, d'une phase. */
export const SEANCES_REFRESH_EVENT = "bizcoach:seances-refresh";
export const notifySeancesChanged = () => { try { window.dispatchEvent(new Event(SEANCES_REFRESH_EVENT)); } catch { /* noop */ } };

export function MySeances({ agents, onStartTraining, onNavigate, onNavigateToChallenge, refreshKey = 0, profileType }: {
  agents: AIAgent[];
  /** Profil de la personne connectée : choisit le texte « pas encore accès » (Q6). */
  profileType?: string | null;
  onStartTraining: (agent: AIAgent, challengeId?: string, options?: { bypassPresentation?: boolean }) => void;
  onNavigate: (page: string) => void;
  onNavigateToChallenge?: (id: string) => void;
  /** Change quand une séance vient d'être faite ailleurs : le bloc se recharge. */
  refreshKey?: number;
}) {
  const { t, i18n } = useTranslation();
  const noAccessText = t(noAccessKey(profileType), { place: `${t("nav.myAccount")} › ${t("account.licences")}` });
  // S'exercer fermé (interrupteur, `drill-visibility.ts`) : aucune ligne « Exercice », même servie par erreur.
  const drillOuvert = useDrillVisible();
  const [data, setData] = useState<MySeancesPayload | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  // Décision 3.8 : le bloc est TOUJOURS affiché — chargement, erreur (Réessayer, puis Prévenir le
  // support après 3 échecs), accès absent, liste vide. Un rechargement raté garde la liste affichée.
  const [loadError, setLoadError] = useState<unknown>(null);
  const { failures, fail, reset } = useFailureCount();

  const lastLoad = useRef(0);
  const load = (force = false) => {
    // Retour d'onglet + fin de partie + montage arrivent groupés : un chargement par 5 s suffit.
    if (!force && Date.now() - lastLoad.current < 5000) return Promise.resolve();
    lastLoad.current = Date.now();
    return planApi.mine()
      .then((d) => { setData(d); setLoadError(null); reset(); })
      .catch((e) => { setLoadError(e ?? new Error("load failed")); fail(); });
  };
  useEffect(() => { void load(true); }, [refreshKey]);
  // La file bouge sans que la page change : une séance soldée ailleurs (challenge,
  // parcours, autre onglet) ou une partie terminée. On recharge quand l'écran
  // redevient visible et sur l'événement émis à la fin d'une partie / d'un exercice.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") void load(); };
    const onDone = () => void load();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener(SEANCES_REFRESH_EVENT, onDone);
    return () => { document.removeEventListener("visibilitychange", onVisible); window.removeEventListener(SEANCES_REFRESH_EVENT, onDone); };
  }, []);

  const loc = t("common.dateLocale");
  const ws = useMemo(() => weekStart(new Date()), []);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(ws, i)), [ws]);
  // La semaine suivante aussi : un déplacement hors de la semaine en cours suit la règle 3.5 bis
  // (libre ou demande, selon le profil ; le serveur tranche et l'écran dit à qui part la demande).
  const nextDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(ws, 7 + i)), [ws]);
  const fmtDay = (d: Date) => d.toLocaleDateString(loc, { weekday: "short", day: "numeric" });

  // Le bilan d'une campagne terminée reste affiché jusqu'à ce qu'on le referme (par navigateur).
  const [dismissedReport, setDismissedReport] = useState<string | null>(() => { try { return localStorage.getItem("bizcoach_report_dismissed"); } catch { return null; } });
  const dismissReport = (id: string) => { setDismissedReport(id); try { localStorage.setItem("bizcoach_report_dismissed", id); } catch { /* noop */ } };

  const frame = (children: ReactNode) => (
    <div data-tour="my-seances" className="rounded-2xl border p-5 shadow-[var(--elev-1)]" style={{ background: "#EDF5FC", borderColor: "#A6D3F9" }}>
      <h2 className="mb-2 flex items-center gap-2 font-bold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
        <span className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: "#DCEBFA" }}><CalendarDays className="h-4 w-4" style={{ color: "#2C6C9E" }} /></span>
        {t("seances.title")}
      </h2>
      {children}
    </div>
  );
  /** Entraînement libre : les simulateurs de l'écran courant s'il y en a, sinon la page M'entraîner. */
  const goFreeTraining = () => {
    const el = typeof document !== "undefined" ? document.querySelector('[data-tour="dashboard-simulators"], [data-tour="studio-simulators"]') : null;
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    else onNavigate("m-entrainer");
  };

  if (!data) {
    if (loadError) {
      return frame(<BlockingError screen="my-sessions" message={t("seances.loadError")} error={loadError} failures={failures} onRetry={() => load(true)} />);
    }
    return frame(
      <div className="flex flex-col gap-2" aria-busy="true" aria-label={t("common.loading")}>
        {[0, 1, 2].map((i) => <div key={i} className="h-10 animate-pulse rounded-lg bg-white/70" />)}
      </div>,
    );
  }
  // Z4 : accès du compte terminé — un seul message à la place de tout le bloc.
  if (data.accessEnded) return frame(<AccessEndedNotice canResubscribe={canBuyLicences(profileType)} />);
  const { queue, recentDone, catchUps, counters, streak } = data;
  const report = data.closedCampaign && data.closedCampaign.id !== dismissedReport ? data.closedCampaign : null;
  // Décision 3.7 : sans aucun simulateur jouable, le bloc le dit (et vers qui se tourner).
  if (data.hasAccess === false && queue.length === 0 && catchUps.length === 0 && !report) {
    return frame(<p className="rounded-lg bg-white px-3 py-2.5 text-sm" style={{ color: "#384872" }}>{noAccessText}</p>);
  }
  // Liste vide : un message positif et l'entraînement libre, jamais un bloc qui disparaît.
  if (queue.length === 0 && recentDone.length === 0 && catchUps.length === 0 && !report) {
    return frame(
      <div className="flex flex-col items-start gap-2 rounded-lg bg-white px-3 py-3">
        <p className="text-sm font-semibold" style={{ color: "#1E7A3C" }}>{t("seances.emptyTitle")}</p>
        <p className="text-xs" style={{ color: "#5b678a" }}>{t("seances.emptyBody")}</p>
        <Button size="sm" onClick={goFreeTraining}><Play className="h-3.5 w-3.5" />{t("seances.freeTraining")}</Button>
      </div>,
    );
  }

  const agentOf = (id: string) => agents.find((a) => a.id === id);

  /** Le clic fait ce que la ligne dit — jamais une liste générique. */
  const start = (s: Seance) => {
    const tg = s.target;
    setNote(null);
    if (tg.kind === "challenge" && tg.challengeId && onNavigateToChallenge) { onNavigateToChallenge(tg.challengeId); return; }
    if (tg.kind === "path" && tg.pathId) {
      try { localStorage.setItem("bizcoach_focus_path_id", tg.pathId); } catch { /* noop */ }
      onNavigate("certification-paths"); return;
    }
    if (tg.kind === "assessment") {
      try { localStorage.setItem("bizcoach_open_eval", "1"); } catch { /* noop */ }
      onNavigate("certification-paths"); return;
    }
    if (tg.kind === "drill" && tg.criterionKey) {
      launchDrill({ criterionKey: tg.criterionKey, criterionLabel: tg.criterionLabel ?? tg.criterionKey, simulatorId: tg.simulatorId, simulatorLabel: tg.simulatorName, origin: "progress", sourceSessionId: tg.sourceSessionId ?? undefined, exerciseSheetId: tg.exerciseSheetId ?? undefined }, onNavigate as never);
      return;
    }
    const agent = agentOf(tg.simulatorId);
    if (!agent) { setNote(t("seances.cannotStart")); return; }
    try {
      localStorage.setItem("bizcoach_pending_seance", JSON.stringify({
        seanceId: s.id, simulatorId: tg.simulatorId, targetId: tg.targetId ?? null,
        sourceSessionId: tg.kind === "retrain" ? tg.sourceSessionId ?? null : null,
        focusCriterionKey: tg.kind === "retrain" ? tg.criterionKey ?? null : null,
        createdAt: Date.now(),
      }));
    } catch { /* noop */ }
    // Un cas imposé saute la présentation ; un simulateur seul la garde (elle aide à choisir le cas).
    onStartTraining(agent, undefined, { bypassPresentation: !!tg.targetId });
  };

  /** Rattraper = jouer la séance manquée la plus ancienne de la campagne, sur son cas. */
  const startCatchUp = (seanceId: string, simulatorId: string, targetId: string | null) => {
    const agent = agentOf(simulatorId);
    if (!agent) { setNote(t("seances.cannotStart")); return; }
    try {
      localStorage.setItem("bizcoach_pending_seance", JSON.stringify({ seanceId, simulatorId, targetId, sourceSessionId: null, focusCriterionKey: null, createdAt: Date.now() }));
    } catch { /* noop */ }
    onStartTraining(agent, undefined, { bypassPresentation: !!targetId });
  };

  const move = async (s: Seance, day: string) => {
    setBusy(s.id); setNote(null);
    try {
      const r = await planApi.moveMine(s.id, day);
      setNote(r.requested ? t("myWeek.moveRequested", { name: r.requestedTo?.name || t("myWeek.yourManager") }) : null);
      await load(true);
    } catch (e) { setNote(e instanceof Error ? e.message : t("common.error")); }
    finally { setBusy(null); }
  };

  const Row = ({ s, done }: { s: Seance; done?: boolean }) => {
    const st = SOURCE_STYLE[s.source];
    const a = agentOf(s.target.simulatorId);
    const isCampaignDay = s.source === "campaign" && !!s.day;
    return (
      <div className="flex items-center gap-2.5 border-b py-2.5 last:border-0" style={{ borderColor: "#CFE4F6" }}>
        {done ? (
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md" style={{ background: "#7CBF94" }}><Check className="h-3.5 w-3.5 text-white" /></span>
        ) : (
          <span className="shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide" style={{ background: st.bg, color: st.color }}>{t(`seances.source.${s.source}`)}</span>
        )}
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg" style={{ background: "#FFFFFF" }}><SimIcon icon={a?.icon ?? "🎯"} size={16} /></span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold" style={{ color: "#384872" }}>{s.title}</div>
          <div className="truncate text-[11px]" style={{ color: "#5b678a" }}>
            {s.why}
            {s.moveRequestedTo && <> · {t("myWeek.requested", { day: fmtDay(fromISODate(s.moveRequestedTo)) })}</>}
          </div>
          {/* Suggestion ciblée sur un critère (5.2 bis) : « Ce qu'on attend : comportement », définition au survol / toucher. */}
          {!done && (s.source === "evaluation" || s.source === "replay") && (s.target.criterionBehavior ?? "").trim() && (
            <div className="flex items-center gap-1 text-[11px]" style={{ color: "#5b678a" }}>
              {/* Retour à la ligne, jamais tronqué : sur mobile, rien d'autre ne donnerait le comportement en entier. */}
              <span className="min-w-0 line-clamp-2 break-words">{t("criterion.expected")} <i>{s.target.criterionBehavior}</i></span>
              <CriterionHelp help={s.target.criterionHelp} label={s.target.criterionLabel ?? ""} />
            </div>
          )}
        </div>
        {done ? (
          <span className="text-xs font-semibold" style={{ color: "#926B00" }}>+{s.rewardCoins} 🪙</span>
        ) : (
          <>
            {isCampaignDay && (
              <select aria-label={t("myWeek.moveLabel")} className="h-8 max-w-[7.5rem] rounded-md border bg-white px-1.5 text-xs" style={{ borderColor: "#CFE4F6", color: "#384872" }}
                value={[...days, ...nextDays].some((d) => toISODate(d) === s.day) ? s.day! : ""} disabled={busy === s.id}
                onChange={(e) => { if (e.target.value) void move(s, e.target.value); }} id={`seance-move-${s.id}`}>
                {![...days, ...nextDays].some((d) => toISODate(d) === s.day) && <option value="">{fmtDay(fromISODate(s.day!))}</option>}
                <optgroup label={t("myWeek.thisWeek")}>{days.map((d) => <option key={toISODate(d)} value={toISODate(d)}>{fmtDay(d)}</option>)}</optgroup>
                <optgroup label={t("myWeek.nextWeek")}>{nextDays.map((d) => <option key={toISODate(d)} value={toISODate(d)}>{fmtDay(d)}</option>)}</optgroup>
              </select>
            )}
            <button type="button" onClick={() => start(s)} className="flex min-h-[32px] items-center gap-1 rounded-lg px-3 text-xs font-bold text-white" style={{ background: "#1E7A3C" }}>
              <Play className="h-3.5 w-3.5" />{t(s.target.kind === "retrain" ? "seances.replay" : s.target.kind === "drill" ? "seances.exercise" : s.target.kind === "challenge" || s.target.kind === "path" || s.target.kind === "assessment" ? "seances.open" : "seances.start")}
            </button>
          </>
        )}
      </div>
    );
  };

  const doneLine = counters.sinceLastVisit > 0
    ? t("seances.sinceVisit", { count: counters.sinceLastVisit })
    : counters.daysSinceLast == null ? t("seances.firstTime") : t("seances.lastOne", { n: counters.last30Days });

  return (
    <div data-tour="my-seances" className="rounded-2xl border p-5 shadow-[var(--elev-1)]" style={{ background: "#EDF5FC", borderColor: "#A6D3F9" }}>
      <div className="mb-1 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-bold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
          <span className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: "#DCEBFA" }}><CalendarDays className="h-4 w-4" style={{ color: "#2C6C9E" }} /></span>
          {t("seances.title")}
        </h2>
        <span className="text-xs font-semibold tabular-nums" style={{ color: "#2C6C9E" }}>
          {counters.queueDone > 0 ? t("seances.queueCount", { done: counters.queueDone, total: Math.max(counters.queueTotal, counters.queueDone) }) : queue.length > 0 ? t("seances.toDo", { count: queue.length }) : ""}
        </span>
      </div>
      <p className="mb-2 text-xs" style={{ color: "#2C6C9E" }}>{doneLine}{streak.current > 1 ? ` · ${t("seances.streak", { n: streak.current })}` : ""}</p>
      {streak.justFull && <div className="mb-2 rounded-lg bg-white px-2.5 py-1.5 text-xs font-semibold" style={{ color: "#1E7A3C" }}>🎉 {t("seances.fullQueue")}</div>}
      {note && <div className="mb-2 rounded-lg bg-white px-2.5 py-1.5 text-xs" style={{ color: "#926B00" }} role="status">{note}</div>}
      {/* Rechargement raté : la liste déjà affichée reste, l'erreur est dite au-dessus. */}
      {loadError != null && <div className="mb-2"><BlockingError compact screen="my-sessions" action="reload" message={t("seances.loadError")} error={loadError} failures={failures} onRetry={() => load(true)} /></div>}
      {data.hasAccess === false && <p className="mb-2 rounded-lg bg-white px-2.5 py-1.5 text-xs" style={{ color: "#384872" }}>{noAccessText}</p>}
      <div className="flex flex-col">
        {recentDone.slice(0, 1).map((s) => <Row key={s.id} s={s} done />)}
        {queue.filter((s) => drillOuvert || s.target.kind !== "drill").map((s) => <Row key={s.id} s={s} />)}
      </div>
      {catchUps.length > 0 && (
        <div className="mt-2 flex flex-col gap-1">
          {catchUps.map((c) => (
            <div key={c.campaignId} className="flex flex-wrap items-center justify-between gap-2 rounded-lg px-3 py-2 text-xs" style={{ background: "#FFFFFF", color: "#926B00" }}>
              <span>{t("seances.catchUp", { count: c.count, title: c.title })}</span>
              {c.seanceId && c.simulatorId && agentOf(c.simulatorId) ? (
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => startCatchUp(c.seanceId!, c.simulatorId!, c.targetId)}>
                  <Play className="h-3.5 w-3.5" />{t("seances.start")}
                </Button>
              ) : (
                <span className="text-[11px]" style={{ color: "#7C93B0" }}>{t("seances.catchUpHint")}</span>
              )}
            </div>
          ))}
        </div>
      )}
      {data.myCampaign && (
        <div className="mt-3 rounded-xl bg-white px-3 py-2.5">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-xs">
            <span className="font-semibold" style={{ color: "#384872" }}>{t("seances.myCampaign", { title: data.myCampaign.title })}</span>
            <span className="tabular-nums" style={{ color: "#5b678a" }}>
              {t("seances.myCampaignDone", { done: data.myCampaign.done, total: data.myCampaign.total })}
              {data.myCampaign.daysLeft != null && <> · {t("campaign.daysLeftShort", { count: Math.max(0, data.myCampaign.daysLeft) })}</>}
            </span>
          </div>
          {/* « Compétence travaillée : Critère (comportement) » (5.2 bis). */}
          {data.myCampaign.criterionLabel && (
            <div className="mt-1 text-[11px]" style={{ color: "#384872" }}>
              {t("criterion.skillWorked")}{" "}
              <CriterionInline label={data.myCampaign.criterionLabel} behavior={data.myCampaign.criterionBehavior} help={data.myCampaign.criterionHelp} />
            </div>
          )}
          {data.myCampaign.criterionLabel && (data.myCampaign.levelStart != null || data.myCampaign.levelNow != null) && (
            <div className="mt-1 flex items-center justify-between gap-3">
              <span className="text-[11px] tabular-nums" style={{ color: "#5b678a" }}>
                {data.myCampaign.levelStart != null ? data.myCampaign.levelStart.toFixed(1) : "—"} → <b style={{ color: "#1E7A3C" }}>{data.myCampaign.levelNow != null ? data.myCampaign.levelNow.toFixed(1) : "…"}</b>{data.myCampaign.levelTarget != null && <> → {data.myCampaign.levelTarget.toFixed(1)}</>}
              </span>
              <Sparkline points={data.myCampaign.curve} target={data.myCampaign.levelTarget} width={120} height={32} />
            </div>
          )}
        </div>
      )}
      {/* Célébrer : la campagne est finie, voici ce que TU as fait monter. Jamais le niveau des autres. */}
      {report && (() => {
        const me = report.members[0];
        const delta = me?.delta ?? null;
        const verdictKey = report.outcome === "stopped" ? "stopped" : (report.outcome ?? report.verdict);
        return (
          <div className="mt-3 rounded-xl px-3 py-2.5" style={{ background: "#F1F9F4", border: "1px solid #7CBF94" }}>
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-xs">
              <span className="font-semibold" style={{ color: "#1E7A3C" }}>{t("seances.report.title", { title: report.title })}</span>
              <span className="flex items-center gap-2">
                {verdictKey && <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ background: "#fff", color: "#1E7A3C" }}>{t(`campaign.outcome.${verdictKey}`)}</span>}
                <button type="button" onClick={() => dismissReport(report.id)} className="text-[11px]" style={{ color: "#7C93B0" }} aria-label={t("common.close")}>✕</button>
              </span>
            </div>
            <div className="mt-1 flex items-center justify-between gap-3">
              <div className="text-[12px]" style={{ color: "#384872" }}>
                {report.objective && me && (me.levelStart != null || me.levelEnd != null) ? (
                  <span className="tabular-nums">
                    <CriterionInline label={report.objective.criterionLabel} behavior={report.objective.criterionBehavior} help={report.objective.criterionHelp} /> : {me.levelStart != null ? me.levelStart.toFixed(1) : "—"} → <b style={{ color: "#1E7A3C" }}>{me.levelEnd != null ? me.levelEnd.toFixed(1) : "—"}</b>
                    {delta != null && delta > 0 && <b style={{ color: "#1E7A3C" }}> (+{delta.toFixed(1)})</b>}
                  </span>
                ) : (
                  <span>{t("seances.report.noMeasure")}</span>
                )}
                <div className="text-[11px]" style={{ color: "#5b678a" }}>
                  {t("seances.report.sessions", { done: me?.sessionsDone ?? 0, played: me?.played ?? 0 })}
                  {report.me?.rank != null && report.me.of >= 2 && <> · {t("seances.report.rank", { rank: report.me.rank, of: report.me.of })}</>}
                </div>
                <div className="mt-1 text-[11px] font-medium" style={{ color: "#1E7A3C" }}>{delta != null && delta > 0 ? t("seances.report.keepGoing") : t("seances.report.nextStep")}</div>
              </div>
              {(report.me?.curve.length ?? 0) >= 2 && <Sparkline points={report.me!.curve} target={report.objective?.levelTarget ?? null} width={120} height={32} />}
            </div>
          </div>
        );
      })()}
      <div className="mt-3 flex items-center justify-between border-t pt-3" style={{ borderColor: "#CFE4F6" }}>
        <span className="text-[11px]" style={{ color: "#7C93B0" }}>{t("seances.hint")}</span>
        <button type="button" onClick={() => onNavigate("m-entrainer")} className="flex min-h-[32px] items-center gap-1 text-xs font-semibold" style={{ color: "#2C6C9E" }}>{t("seances.somethingElse")}<ChevronRight className="h-3.5 w-3.5" /></button>
      </div>
    </div>
  );
}
